import { Group, Layer, Line, Rect } from 'react-konva'
import type Konva from 'konva'
import { useEditor, activeLevel } from '@/state/store'
import type { Opening, PolygonPath } from '@/model/types'
import { compoundCenterline } from '@/lib/walls'
import {
  clampOffset, offsetOnEdge, openingFrame,
  opWidth, opSwing, opHinge, opKinematics, opLeaves,
} from '@/lib/openings'

const GOLD = '#fbbf24'
const DOOR_STROKE = '#cfd6e6'
const GATE_STROKE = '#d9cfa8'
const WINDOW_STROKE = '#a0b0cc'

interface Item {
  ownerId: string
  opening: Opening
  polygon: PolygonPath
}

export function OpeningLayer() {
  const level = useEditor(activeLevel)
  const plot = useEditor((s) => s.project.plot)
  const standards = useEditor((s) => s.project.settings.standards)
  const scale = useEditor((s) => s.view.scale)
  const tool = useEditor((s) => s.tool)

  const items: Item[] = []
  for (const sp of level.spaces) {
    for (const op of sp.openings) items.push({ ownerId: sp.id, opening: op, polygon: sp.polygon })
  }
  const cw = plot.compoundWall
  if (cw?.enabled && cw.openings.length) {
    const centerline = compoundCenterline(plot.boundary, cw.inset)
    for (const op of cw.openings) items.push({ ownerId: 'compound', opening: op, polygon: centerline })
  }

  if (items.length === 0) return null

  return (
    <Layer listening={tool === 'select'}>
      {items.map((it) => (
        <OpeningSymbol
          key={it.opening.id}
          item={it}
          scale={scale}
          wallThickness={it.ownerId === 'compound' ? (cw?.thickness ?? 0.5) : standards.exteriorWallThickness}
        />
      ))}
    </Layer>
  )
}

function OpeningSymbol({ item, scale, wallThickness }: { item: Item; scale: number; wallThickness: number }) {
  const { ownerId, opening, polygon } = item
  const update = useEditor((s) => s.update)
  const beginHistory = useEditor((s) => s.beginHistory)
  const select = useEditor((s) => s.select)
  const selection = useEditor((s) => s.selection)
  const snapInc = useEditor((s) => s.project.settings.gridSnap)

  const selected = selection?.kind === 'opening' && selection.id === opening.id
  const frame = openingFrame(polygon, opening.edgeIndex, opening.offset)
  if (!frame) return null

  const w = opWidth(opening)
  const t = wallThickness
  const px = (n: number) => n / scale
  const isGate = opening.familyId === 'gate'
  const isWindow = opening.familyId === 'window'
  const stroke = selected ? GOLD : isGate ? GATE_STROKE : isWindow ? WINDOW_STROKE : DOOR_STROKE
  const sw = px(selected ? 2.4 : 1.6)

  function mutateOffset(newOffset: number, transient: boolean) {
    update((d) => {
      const op = findOpening(d, ownerId, opening.id)
      if (op) op.offset = newOffset
    }, { transient })
  }

  function onDragMove(e: Konva.KonvaEventObject<DragEvent>) {
    const stage = e.target.getStage()
    const p = stage?.getRelativePointerPosition()
    if (!p) return
    const off = offsetOnEdge(polygon, opening.edgeIndex, p)
    const snapped = Math.round(off / snapInc) * snapInc
    const clamped = clampOffset(polygon, opening.edgeIndex, w, snapped)
    if (clamped !== null) mutateOffset(clamped, true)
  }

  let children: React.ReactNode
  if (isWindow) {
    children = windowSymbol(opening, w, t, stroke, sw)
  } else if (isGate) {
    children = gateSymbol(opening, w, t, stroke, sw, scale)
  } else {
    children = doorSymbol(opening, w, t, stroke, sw)
  }

  return (
    <Group
      x={frame.center.x}
      y={frame.center.y}
      rotation={(frame.angle * 180) / Math.PI}
      draggable
      onMouseDown={(e) => {
        e.cancelBubble = true
        select({ kind: 'opening', ownerId, id: opening.id })
      }}
      onDragStart={() => beginHistory()}
      onDragMove={onDragMove}
    >
      <Rect x={-w / 2} y={-t / 2} width={w} height={t} fill="rgba(0,0,0,0.01)" />
      {selected && (
        <Rect x={-w / 2} y={-t / 2} width={w} height={t}
          stroke={GOLD} strokeWidth={px(1)} dash={[px(3), px(3)]} />
      )}
      {children}
    </Group>
  )
}

function jambTicks(w: number, t: number, stroke: string, sw: number) {
  return [
    <Line key="jl" points={[-w / 2, -t / 2, -w / 2, t / 2]} stroke={stroke} strokeWidth={sw} />,
    <Line key="jr" points={[w / 2, -t / 2, w / 2, t / 2]} stroke={stroke} strokeWidth={sw} />,
  ]
}

function windowSymbol(opening: Opening, w: number, t: number, stroke: string, sw: number) {
  const style = (opening.params.style as string) ?? 'plain'
  const gy = t * 0.18
  const out: React.ReactNode[] = [
    ...jambTicks(w, t, stroke, sw),
    <Line key="g1" points={[-w / 2, -gy, w / 2, -gy]} stroke={stroke} strokeWidth={sw} />,
    <Line key="g2" points={[-w / 2,  gy, w / 2,  gy]} stroke={stroke} strokeWidth={sw} />,
  ]
  if (style === 'grid') {
    // add a vertical mullion in the middle
    out.push(
      <Line key="mv" points={[0, -t / 2, 0, t / 2]} stroke={stroke} strokeWidth={sw * 0.6} />
    )
  }
  return out
}

function singleLeafArc(
  hx: number, dirToOther: number, sideSign: number,
  w: number, stroke: string, sw: number, dash?: number[],
) {
  const a0 = (sideSign * Math.PI) / 2
  const a1 = dirToOther > 0 ? 0 : Math.PI
  let delta = a1 - a0
  while (delta > Math.PI) delta -= 2 * Math.PI
  while (delta < -Math.PI) delta += 2 * Math.PI
  const arc: number[] = []
  const N = 14
  for (let i = 0; i <= N; i++) {
    const a = a0 + (delta * i) / N
    arc.push(hx + w * Math.cos(a), w * Math.sin(a))
  }
  return [
    <Line key={`leaf${hx}`} points={[hx, 0, hx, sideSign * w]} stroke={stroke} strokeWidth={sw} dash={dash} />,
    <Line key={`arc${hx}`}  points={arc}                        stroke={stroke} strokeWidth={sw} dash={dash} />,
  ]
}

function doorSymbol(opening: Opening, w: number, t: number, stroke: string, sw: number) {
  const hinge = opHinge(opening)
  const swing = opSwing(opening)
  const leaves = opLeaves(opening)
  const hx = hinge === 'left' ? -w / 2 : w / 2
  const dirToOther = hinge === 'left' ? 1 : -1
  const sideSign = swing === 'in' ? 1 : -1

  const out: React.ReactNode[] = [...jambTicks(w, t, stroke, sw)]

  if (leaves === 2) {
    // Double door: one leaf from each jamb
    const s2 = swing === 'in' ? 1 : -1
    out.push(...singleLeafArc(-w / 2,  1, s2, w / 2, stroke, sw))
    out.push(...singleLeafArc( w / 2, -1, s2, w / 2, stroke, sw))
  } else {
    out.push(...singleLeafArc(hx, dirToOther, sideSign, w, stroke, sw))
  }
  return out
}

function gateSymbol(opening: Opening, w: number, t: number, stroke: string, sw: number, scale: number) {
  const kinematics = opKinematics(opening)
  const dash: [number, number] = [12 / scale, 8 / scale]
  const ps = Math.max(t, 0.4)

  const posts = [
    <Rect key="pl" x={-w / 2 - ps / 2} y={-ps / 2} width={ps} height={ps} fill={stroke} />,
    <Rect key="pr" x={w / 2 - ps / 2}  y={-ps / 2} width={ps} height={ps} fill={stroke} />,
  ]

  if (kinematics === 'sliding') {
    // Panel drawn offset along the wall (slides to the right side), with a direction arrow
    const panelW = w * 0.85
    return [
      ...jambTicks(w, t, stroke, sw),
      ...posts,
      // Sliding panel: a thin rect beside the opening, shifted right
      <Rect key="panel" x={w / 2 - panelW * 0.1} y={-t * 0.8} width={panelW} height={t * 0.6}
        stroke={stroke} strokeWidth={sw} dash={dash} fill="transparent" />,
      // Arrow pointing right to indicate slide direction
      <Line key="arr"
        points={[w * 0.1, 0, w * 0.4, 0, w * 0.3, -t * 0.5, w * 0.4, 0, w * 0.3, t * 0.5]}
        stroke={stroke} strokeWidth={sw} />,
    ]
  }

  if (kinematics === 'biparting') {
    // Two half-leaves meeting in the center, each swinging out
    const s2 = opSwing(opening) === 'in' ? 1 : -1
    return [
      ...jambTicks(w, t, stroke, sw),
      ...posts,
      ...singleLeafArc(-w / 2,  1, s2, w / 2, stroke, sw, dash),
      ...singleLeafArc( w / 2, -1, s2, w / 2, stroke, sw, dash),
    ]
  }

  // swing (default)
  const hinge = opHinge(opening)
  const swing = opSwing(opening)
  const hx = hinge === 'left' ? -w / 2 : w / 2
  const dirToOther = hinge === 'left' ? 1 : -1
  const sideSign = swing === 'in' ? 1 : -1

  return [
    ...jambTicks(w, t, stroke, sw),
    ...posts,
    ...singleLeafArc(hx, dirToOther, sideSign, w, stroke, sw, dash),
  ]
}

function findOpening(draft: any, ownerId: string, openingId: string): Opening | undefined {
  if (ownerId === 'compound') {
    return draft.plot.compoundWall?.openings.find((o: Opening) => o.id === openingId)
  }
  for (const lvl of draft.levels) {
    const sp = lvl.spaces.find((s: any) => s.id === ownerId)
    if (sp) return sp.openings.find((o: Opening) => o.id === openingId)
  }
  return undefined
}
